import { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import {
  Button,
  Card,
  Form,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  notification,
} from 'antd';
import { PageTemplate } from '@/shared/ui/templates';
import { DiContainer } from '@@/di/di-container';
import { DlpService } from '../../services/dlp.service';
import { $elevators, $lots, fetchElevatorsFx, fetchLotsFx } from '../../store/dlp.store';
import { DlpLot } from '../../ts';

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  draft: { text: 'Черновик', color: 'default' },
  pending_consent: { text: 'На согласовании', color: 'processing' },
  reserved: { text: 'Забронирована', color: 'gold' },
  formed: { text: 'Сформирована', color: 'blue' },
  certified: { text: 'Сертифицирована', color: 'green' },
  shipped: { text: 'Отгружена', color: 'cyan' },
  cancelled: { text: 'Отменена', color: 'red' },
};

export const DlpLotsPage = () => {
  const svc = () => DiContainer.get(DlpService);
  const lots = useStore($lots);
  const elevators = useStore($elevators);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const reload = () => {
    fetchLotsFx();
    fetchElevatorsFx();
  };

  useEffect(() => {
    reload();
  }, []);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setLoading(true);
    try {
      await fn();
      notification.success({ message: ok });
      reload();
    } catch (e: any) {
      notification.error({ message: e?.error || e?.message || 'Ошибка операции' });
    } finally {
      setLoading(false);
    }
  };

  const onCreate = (values: any) => {
    run(
      () =>
        svc().createDraft({
          offer_id: Number(values.offer_id),
          elevator_id: Number(values.elevator_id),
          target_weight: Number(values.target_weight),
        }),
      'Черновик партии создан',
    ).then(() => form.resetFields());
  };

  const actions = (lot: DlpLot) => {
    const items: JSX.Element[] = [];
    if (lot.status === 'draft') {
      items.push(
        <Button key="submit" size="small" onClick={() => run(() => svc().submit(lot.id), 'Заявки отправлены')}>
          Отправить заявки
        </Button>,
      );
    }
    if (lot.status === 'pending_consent') {
      items.push(
        <Button key="reserve" size="small" type="primary" onClick={() => run(() => svc().reserve(lot.id), 'Забронировано')}>
          Забронировать
        </Button>,
      );
    }
    if (lot.status === 'reserved') {
      items.push(
        <Button key="form" size="small" type="primary" onClick={() => run(() => svc().form(lot.id), 'Партия сформирована')}>
          Сформировать
        </Button>,
      );
    }
    if (!['shipped', 'cancelled'].includes(lot.status)) {
      items.push(
        <Button key="cancel" size="small" danger onClick={() => run(() => svc().cancel(lot.id), 'Отменено')}>
          Отменить
        </Button>,
      );
    }
    return <Space wrap>{items}</Space>;
  };

  const columns = [
    { title: 'Код', dataIndex: 'code', key: 'code' },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => {
        const st = STATUS_LABEL[s] || { text: s, color: 'default' };
        return <Tag color={st.color}>{st.text}</Tag>;
      },
    },
    { title: 'Целевой вес', dataIndex: 'target_weight', key: 'target_weight' },
    { title: 'Сегментов', dataIndex: 'items_count', key: 'items_count' },
    { title: 'Действия', key: 'actions', render: (_: any, lot: DlpLot) => actions(lot) },
  ];

  return (
    <PageTemplate header="Цифровые партии">
      <div style={{ padding: 16 }}>
        <Card title="Собрать новую партию" style={{ marginBottom: 16 }}>
          <Form form={form} layout="inline" onFinish={onCreate}>
            <Form.Item name="elevator_id" label="Элеватор" rules={[{ required: true }]}>
              <Select
                style={{ minWidth: 220 }}
                placeholder="Выберите элеватор"
                options={elevators.map((e) => ({ value: e.id, label: e.name }))}
              />
            </Form.Item>
            <Form.Item name="offer_id" label="Оффер (ID)" rules={[{ required: true }]}>
              <InputNumber min={1} placeholder="ID оффера" />
            </Form.Item>
            <Form.Item name="target_weight" label="Целевой вес" rules={[{ required: true }]}>
              <InputNumber min={1} placeholder="кг" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Подобрать
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={lots}
          pagination={{ pageSize: 20 }}
        />
      </div>
    </PageTemplate>
  );
};
